import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';

const UPS = 100000000;
const FEDEX = 100000001;

export const isParcelCarrier = (shippingMethod: number | null | undefined) =>
  shippingMethod === UPS || shippingMethod === FEDEX;

export type AnimalShipmentConflict = {
  animalId: string;
  animalName: string | null;
  animalType: string;
  shipmentId: string;
  shipmentNumber: string | null;
  shipmentStatus: number | null;
};

export type LinkAnimalsToShipmentInput = {
  shipmentId: string;
  animalIds: string[];
  overrideExistingShipment?: boolean;
};

export type LinkAnimalsToShipmentPayload = {
  code: number;
  success: boolean;
  message: string | null;
  requiresOverride: boolean;
  conflicts: AnimalShipmentConflict[];
};

export async function linkAnimalsToShipment(
  token: AccessToken,
  input: LinkAnimalsToShipmentInput,
): Promise<LinkAnimalsToShipmentPayload> {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  const response = await fetch(
    `${import.meta.env.VITE_API_BASE_URL}/api/v1/animal/add-animals-to-shipment`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    },
  );
  return (await response.json()) as LinkAnimalsToShipmentPayload;
}

export const useLinkAnimalsToShipment = () => {
  const queryClient = useQueryClient();
  const token = useAccessToken();
  return useMutation({
    mutationFn: (input: LinkAnimalsToShipmentInput) =>
      linkAnimalsToShipment(token, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipments'] });
    },
  });
};
